import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';
import { Box, Grid, InputAdornment } from '@mui/material';
import {
  asUsfOrZero,
  handleOnKeyPress,
  limitToCharLength,
} from 'spw-shared/utils';
import { Row } from 'spw-shared/globalComponents/grid';
import { SPWClickAwayTooltip } from 'spw-shared/globalComponents';
import { useDispatch } from 'react-redux';
import { actions } from 'spw-redux/modules/calculators/actions';

import { MAX_MACRO_HEADCOUNT } from 'spw-constants';
import { CalculatorType } from 'spw-redux/modules/calculators/types';
import {
  FactorTitle,
  BigNumber,
  Label,
  HeadcountInput,
  Ratio,
  Operator,
  NumberOp,
  InputWrapper,
} from './styles';
import { formatWithDecimals } from './utils';

interface InputStyle {
  width: number;
}

export const TARGET_USF_LABEL = 'Target USF';

// alison  5:35 PM
// Vacancy Factor %: 0.0 min and 99.0 max
// Headcount Utilization %: 1 min and 100 max
// Desk Sharing Ratio: 1: 0.10 min and 1: 9.99 max
// Growth Rate %: -10.0 min and 10.0 max
const FACTORS_VALIDATIONS: {
  [factor: string]: {
    defaultFactorValueWhenEmpty: number;
    maxFactorValue: number;
    minFactorValue: number;
    decimalsAllowed: number;
  };
} = {
  vacancyFactor: {
    minFactorValue: 0,
    defaultFactorValueWhenEmpty: 0,
    maxFactorValue: 99.0,
    decimalsAllowed: 1,
  },
  headcountUtilization: {
    minFactorValue: 1,
    defaultFactorValueWhenEmpty: 100,
    maxFactorValue: 100,
    decimalsAllowed: 0,
  },
  deskSharingRatio: {
    minFactorValue: 0.1,
    defaultFactorValueWhenEmpty: 1,
    maxFactorValue: 9.99,
    decimalsAllowed: 2,
  },
  targetUsf: {
    minFactorValue: 1,
    defaultFactorValueWhenEmpty: 1,
    maxFactorValue: MAX_MACRO_HEADCOUNT,
    decimalsAllowed: 0,
  },
};

interface HeadcountProps {
  title?: string;
  base: number;
  baseInput?: ReactElement;
  leftLabel?: string;
  rightLabel?: string;
  centralLabel?: string;
  centralInputClassName?: string;
  showRatio?: boolean;
  tooltip?: JSX.Element;
  tooltipWidth?: number;
  tooltipHeight?: number;
  operator?: string;
  onChange?: (value: number) => void;
  inputStyles?: InputStyle;
  factorName?: string;
  hideOp?: boolean;
  factorValue?: string;
  resultValue?: number;
  disabled: boolean;
  setSecondary?: (v: number) => void;
  calculatorType?: CalculatorType;
  showDecimals: boolean;
  testingIdPrefix?: string;
}

const Headcount: React.FC<HeadcountProps> = ({
  title,
  base,
  baseInput,
  leftLabel,
  rightLabel,
  showRatio,
  tooltip,
  tooltipWidth,
  tooltipHeight,
  operator,
  factorName,
  hideOp,
  factorValue = '0',
  resultValue,
  inputStyles,
  centralLabel,
  centralInputClassName = '',
  disabled,
  setSecondary,
  showDecimals,
  testingIdPrefix,
}) => {
  const {
    minFactorValue,
    maxFactorValue,
    defaultFactorValueWhenEmpty,
    decimalsAllowed,
  } = FACTORS_VALIDATIONS[factorName] || {};

  const setFactorValueDecimalFormat = useCallback(() => {
    if (decimalsAllowed) return Number(factorValue).toFixed(decimalsAllowed);
    return factorValue;
  }, [factorValue, decimalsAllowed]);

  const [editValue, setEditValue] = useState(setFactorValueDecimalFormat());

  const placement = useMemo(() => {
    switch (factorName) {
      case 'vacancyFactor':
      case 'headcountUtilization':
        return 'bottom-right';

      case 'deskSharingRatio':
        return 'bottom-left';

      default:
        return 'top-left';
    }
  }, [factorName]);

  useEffect(() => {
    setEditValue(setFactorValueDecimalFormat());
  }, [setFactorValueDecimalFormat, factorValue]);

  const dispatch = window.SPWExport ? Function.prototype : useDispatch();

  const setFactor = (value: any) => {
    dispatch(actions.setScenarioValue({ factor: factorName, value }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = limitToCharLength(
      event.target.value,
      MAX_MACRO_HEADCOUNT
    );
    setEditValue(formatWithDecimals(inputValue, decimalsAllowed));
  };

  const validateAndGetFactorValue = (numericFactorValue: number) => {
    const stringFactorValue = numericFactorValue.toString();
    const isFactorValueEmpty = stringFactorValue === '';
    if (isFactorValueEmpty)
      return defaultFactorValueWhenEmpty.toFixed(decimalsAllowed);
    const isFactorValueLowerThanMin = numericFactorValue < minFactorValue;
    if (isFactorValueLowerThanMin)
      return minFactorValue.toFixed(decimalsAllowed);
    const isFactorValueGreaterThanMax = numericFactorValue > maxFactorValue;
    if (isFactorValueGreaterThanMax)
      return maxFactorValue.toFixed(decimalsAllowed);
    return numericFactorValue.toFixed(decimalsAllowed);
  };

  const handleOnBlur = () => {
    let numVal = parseFloat(editValue);
    if (_.isNaN(numVal)) numVal = defaultFactorValueWhenEmpty;

    const newFactorValue = validateAndGetFactorValue(numVal);
    setFactor(parseFloat(newFactorValue));
    setEditValue(newFactorValue);
    if (setSecondary) {
      setSecondary(parseInt(numVal.toString(), 10));
    }
  };

  const baseComponent = baseInput || (
    <Grid
      item
      xs={6}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <InputWrapper>
        <Label data-testid={`${testingIdPrefix}_left_label`}>{leftLabel}</Label>
        <NumberOp>
          <BigNumber
            className="large first-term"
            data-testid={`${testingIdPrefix}_left_value`}
          >
            {asUsfOrZero(base, showDecimals)}
          </BigNumber>
          {operator && <Operator className="operator">{operator}</Operator>}
        </NumberOp>
      </InputWrapper>
    </Grid>
  );

  return (
    <Grid container>
      <Grid item xs={12}>
        {(title || tooltip) && (
          <Row alignItems="center" marginBottom="6px">
            {title && (
              <FactorTitle data-testid={testingIdPrefix}>{title}</FactorTitle>
            )}
            {tooltip && tooltipHeight && tooltipWidth && (
              <SPWClickAwayTooltip
                height={tooltipHeight}
                width={tooltipWidth}
                placement={placement}
              >
                {tooltip}
              </SPWClickAwayTooltip>
            )}
          </Row>
        )}
      </Grid>

      <Grid container wrap="nowrap" justifyContent="space-between">
        {baseComponent}
        <Grid
          item
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          <Label data-testid={`${testingIdPrefix}_middle_label`}>
            {centralLabel}
          </Label>
          <Box minWidth={75}>
            <HeadcountInput
              style={inputStyles}
              value={editValue}
              disabled={disabled}
              onKeyPress={handleOnKeyPress}
              onBlur={handleOnBlur}
              onChange={handleInputChange}
              InputProps={{
                endAdornment: !showRatio ? (
                  <InputAdornment position="end">%</InputAdornment>
                ) : null,
                startAdornment: !hideOp && showRatio ? <Ratio>1:</Ratio> : null,
              }}
              className={`${
                !showRatio ? 'with-end-adornment' : ' '
              }second-term ${centralInputClassName} ${
                centralLabel === TARGET_USF_LABEL ? 'floating-label' : ''
              }`}
              data-testid={`${testingIdPrefix}_middle_value`}
            />
          </Box>
        </Grid>
        <Grid
          item
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          <span>
            <NumberOp>
              <Operator className="equal-sign">=</Operator>
            </NumberOp>
          </span>
        </Grid>
        <Grid
          item
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          <InputWrapper>
            <Label data-testid={`${testingIdPrefix}_right_label`}>
              {rightLabel}
            </Label>
            <NumberOp>
              <div data-testid={`${testingIdPrefix}_right_value`}>
                <BigNumber className="result">
                  {asUsfOrZero(resultValue, showDecimals)}
                </BigNumber>
              </div>
            </NumberOp>
          </InputWrapper>
        </Grid>
      </Grid>
    </Grid>
  );
};

Headcount.defaultProps = {
  showRatio: false,
  leftLabel: 'Headcount',
  rightLabel: 'Headcount',
  hideOp: false,
};

export default Headcount;
